import { useMemo } from "react";
import { useSummary } from "../../hooks/useSummary";
import { useCells } from "../../hooks/useCells";
import { useUIStore } from "../../store/uiStore";
import { headerPalette, type HeaderPalette } from "../../lib/headerTheme";

export function SummaryBanner() {
  const { activeTick } = useUIStore();
  const theme = useUIStore((s) => s.theme);
  const palette = headerPalette(theme);
  const { summary, isLoading } = useSummary(activeTick);
  const { geojson } = useCells(activeTick);

  // "At-risk" = alert_triggered (server-authoritative, per-community
  // threshold). Each SVI quintile has its own cutoff (Q1≈34%, Q5≈45%),
  // so a single global cutoff would mislabel a chunk of cells.
  const atRiskCount = useMemo(() => {
    if (!geojson?.features) return null;
    return geojson.features.filter(
      (f) => f.properties?.alert_triggered === true,
    ).length;
  }, [geojson]);

  if (!activeTick || isLoading || !summary) {
    return null;
  }

  const { equity, vlm } = summary;

  return (
    <div
      style={{
        display: "flex",
        gap: 20,
        fontSize: 12,
        color: palette.statLabel,
        alignItems: "center",
        flexWrap: "nowrap",
        flexShrink: 0,
      }}
    >
      <Stat
        palette={palette}
        label="Total cells"
        value={equity.total_cells.toLocaleString()}
      />
      <Stat
        palette={palette}
        label="Alert-triggered"
        value={atRiskCount != null ? atRiskCount.toLocaleString() : "…"}
        highlight={atRiskCount != null && atRiskCount > 0}
      />
      {/* Flagged-equity / Active-fires / High-vulnerability come from
          equity_findings + vlm_findings tables, which are populated only
          on-demand via chat in v3.1. Suppress when the count is zero so
          the banner doesn't mislead the user with unreliable zeros. */}
      {equity.disparity_flagged_count > 0 && (
        <Stat
          palette={palette}
          label="Flagged equity"
          value={equity.disparity_flagged_count.toLocaleString()}
          highlight
        />
      )}
      {vlm.active_fire_count > 0 && (
        <Stat
          palette={palette}
          label="Active fires"
          value={vlm.active_fire_count.toLocaleString()}
          highlight
        />
      )}
      {equity.high_vulnerability_count > 0 && (
        <Stat
          palette={palette}
          label="High-vulnerability"
          value={equity.high_vulnerability_count.toLocaleString()}
          highlight
        />
      )}
    </div>
  );
}

function Stat({
  palette,
  label,
  value,
  highlight = false,
}: {
  palette: HeaderPalette;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      <span
        style={{
          color: palette.statLabel,
          fontSize: 10,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontWeight: 700,
          color: highlight ? palette.statHighlight : palette.statValue,
          fontSize: 13,
        }}
      >
        {value}
      </span>
    </div>
  );
}
